/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { Authorities } from '../../shared/entities/user';
import { areAllowedRolesPresent } from '../../utils/RBACUtils';
import { IDToken } from '@okta/okta-auth-js';

interface AircraftTailTagProps {
  terminalNum: number; // The terminal number on the aircraft 1 to n or undefined if only 1 terminal
  datapathUuid: string;
  userData: IDToken | undefined;
}

/**
 * This class represents the styling and DOM for the aircraft tail tag on google maps.
 */
const AircraftTailTagDefault = (props: AircraftTailTagProps): React.ReactComponentElement<'div', {}> => {
  const { terminalNum, datapathUuid, userData } = props;

  return (
    <div className={`map__tailtag--detailcontainer map__tailtag--detailcontainer${terminalNum}`}>
      <div className={`map__tailtag--row map__tailtag--row${terminalNum}`}>
        <div
          data-test={`map__tailtag-${datapathUuid}--name--idx-${terminalNum}`}
          className={`map__tailtag--name map__tailtag--name${terminalNum}`}
        />
        <div className="map__tailtag--row">
          <div
            data-test={`map__tailtag-${datapathUuid}--steerablebeamprimaryterminallabel--idx-${terminalNum}`}
            className={`map__tailtag--steerablebeamprimaryterminallabel map__tailtag--steerablebeamprimaryterminallabel${terminalNum}`}
          >
            <div
              data-test={`map__tailtag-${datapathUuid}--steerablebeamprimaryterminallabel__badge-${terminalNum}`}
              className={`map__tailtag--steerablebeamprimaryterminallabel__badge map__tailtag--steerablebeamprimaryterminallabel__badge${terminalNum}`}
            />
          </div>
          {userData && areAllowedRolesPresent(userData, Authorities.ROLE_SIGNAL_QUALITY_VIEW_ONLY) ? (
            <div
              data-test={`map__tailtag-${datapathUuid}--name--idx-${terminalNum}`}
              className={`map__tailtag--name map__tailtag--signal_strength${terminalNum}`}
            />
          ) : null}
        </div>
      </div>
      <div className={`map__tailtag--row map__tailtag--row${terminalNum}`}>
        <div
          data-test={`map__tailtag-${datapathUuid}--beam-${terminalNum}`}
          className={`map__tailtag--beam map__tailtag--beam${terminalNum}`}
        />
      </div>
      <div className={`map__tailtag--row map__tailtag--row${terminalNum}`}>
        <div
          data-test={`map__tailtag-${datapathUuid}--hcx_shortest_distance_to_beam_edge-${terminalNum}`}
          className={`map__tailtag--hcx_shortest_distance_to_beam_edge map__tailtag--hcx_shortest_distance_to_beam_edge${terminalNum}`}
        />
      </div>
      <div className={`map__tailtag--row map__tailtag--row${terminalNum}`}>
        <div
          data-test={`map__tailtag-${datapathUuid}--datarates-${terminalNum}`}
          className={`map__tailtag--flrate map__tailtag--flrate${terminalNum}`}
        />
      </div>
    </div>
  );
};

export default AircraftTailTagDefault;
